<script setup lang="ts">
import {ref, computed, watch, reactive, onMounted, onUnmounted} from "vue";
import StepAboutYou from "./StepAboutYou.vue";
import StepYourTrip from "./StepYourTrip.vue";
import StepTravellersAccommodation from "./StepTravellersAccommodation.vue";
import StepYourStyles from "./StepYourStyles.vue";
import StepEstimatedPrice from "./StepEstimatedPrice.vue";
import { DESKTOP } from "~/constant/common";
import { /*ENQUIRY_STEP_BASE,*/ ENQUIRY_STEP_TESTING_VARIANT, EnquiryStepEnum, keyLocalStorage } from '~/enum/enquiry'
import { isEmpty } from '~/helper'
import { getEnquiryID } from '~/helper/enquiry'
import { debounceAmplitudeScriptTracking } from '~/mixins/amplitude'
import { setCookie } from '~/mixins/cookie'
import { useRoute } from 'vue-router'
// import { getCookie } from '~/mixins/cookie'
import { useEnquiryCaptureStore } from '~/store/pinia/enquiryCapture'
import { useGeneralInfoStore } from '~/store/pinia/generalInfo'

const props = defineProps({
  popupHeight: {
    type: Number,
    default: 0
  },
  isUIPopup: {
    type: Boolean,
    default: false,
  },
  stepBack: {
    type: String as () => EnquiryStepEnum,
    default: null
  }
})
const emits = defineEmits(['completed', 'change-destination'])

const REDIRECT_URL = '/design/confirmation'
// const COUNTRY_CODE_AUSTRALIA = 'AU'
const firstIndex = 0
const enquiryCaptureStore = useEnquiryCaptureStore()
const route = useRoute()
const generalStore = useGeneralInfoStore()

const stepActive = ref<EnquiryStepEnum>(EnquiryStepEnum.Information)
const windowWidth = computed(() => generalStore.generalData.windowWidth)
const isDesktop = computed(() => windowWidth.value > DESKTOP)
const arrayStepEnquiry = computed(() => enquiryCaptureStore.arrayStep)
const indexStepActive = computed(() => {
  return arrayStepEnquiry.value.indexOf(stepActive.value) + 1
})
const enquiryInfos = computed(() => enquiryCaptureStore.enquiryCaptureData)
const enquiryAuthenticate = computed(() => enquiryCaptureStore.enquiryAuthenticate)
const dataStepYourTrip = computed(() => {
  return enquiryCaptureStore.enquiryDataByStep(route.path, EnquiryStepEnum.YourTrip)
})
const dataStepAboutYou = computed(() => {
  return enquiryCaptureStore.enquiryDataByStep(route.path, EnquiryStepEnum.Information)
})
const dataStepEstimatedPrice = computed(() => {
  return enquiryCaptureStore.enquiryDataByStep(route.path, EnquiryStepEnum.YourBudget)
})
const heightFooter = 92
const heightProcessStep = isDesktop.value ? 10 : 4
const heightTitle = isDesktop.value ? 0 : 50
const heightHeader = props.isUIPopup ? heightTitle + heightProcessStep : heightProcessStep

const screenHeight = computed(() => generalStore.generalData.windowHeight)

const heightForm = computed(() => {
  return (props.isUIPopup ? props.popupHeight : screenHeight.value) - (heightHeader + heightFooter)
})

const nextStep = async () => {
  const isFinalStep = stepActive.value === arrayStepEnquiry.value[arrayStepEnquiry.value.length - 1]

  if (isFinalStep) {
    await handleSubmitFinalStep()
    return
  }
  const step = mapNextPreStep(stepActive.value).nextStep
  selectStep(step)
}

const prevStep = () => {
  const step = mapNextPreStep(stepActive.value).prevStep
  selectStep(step)
}

const selectStep = (step: EnquiryStepEnum | null) => {
  if (step === null) return
  stepActive.value = step
  cachingStep(step)
}

const cachingStep = (step: EnquiryStepEnum) => {
  enquiryCaptureStore.cachingStepEnquiry({ step, path: route.path })
}

function mapNextPreStep(step: EnquiryStepEnum) {
  const stepIndex = arrayStepEnquiry.value.indexOf(step)
  return {
    nextStep: stepIndex < arrayStepEnquiry.value.length - 1 ? arrayStepEnquiry.value[stepIndex + 1] : null,
    prevStep: stepIndex > 0 ? arrayStepEnquiry.value[stepIndex - 1] : null,
  }
}

async function handleSubmitFinalStep() {
  try {
    emits('completed')
    const countryCode: any[] =
      dataStepYourTrip.value?.destination && dataStepYourTrip.value?.destination.length
        ? dataStepYourTrip.value?.destination.map((des: any) => des.country_code)
        : []
    const enquiryOption = enquiryCaptureStore.enquiryOptionData
    const userInfo = enquiryCaptureStore.userLoginData

    let dataSubmit = {
      enquiry_type: 'traveller',
      trip_selected: enquiryOption?.trip_name,
      url: `/${enquiryOption?.trip_link}`,
      destinations: enquiryInfos.value?.your_trip?.destinations,
      dates: enquiryInfos.value?.your_trip?.dates,
      enquiry_id: getEnquiryID(route.path),
      expert_id: parseInt(enquiryOption?.designer?.id as string) || enquiryOption?.designer_id || null,
      firstName: enquiryAuthenticate.value?.first_name,
      lastName: enquiryAuthenticate.value?.last_name,
      contact_name: dataStepAboutYou.value?.contactName || enquiryAuthenticate.value?.first_name + ' ' + enquiryAuthenticate.value?.last_name,
      user_type: userInfo?.userType || enquiryAuthenticate.value?.user_type,
      is_first_enquiry: enquiryInfos.value?.is_first_enquiry,
      travellers: enquiryInfos.value?.how?.pax,
      duration: enquiryInfos.value?.your_trip?.length_of_trip,
      note: enquiryInfos.value?.your_style?.suggestion,
      budget: enquiryInfos.value?.your_budget?.budget || enquiryInfos.value?.your_budget?.total_budget,
      budget_currency: enquiryInfos.value?.your_budget?.budget_currency, // new field
      user_id: userInfo?.userId || enquiryAuthenticate.value?.user_id,
      check_answer_all: checkAnswerAll(enquiryInfos.value),
      country_code: countryCode.join(),
    }
    debounceAmplitudeScriptTracking('Submit Enquiry', {
      enquiry_id: getEnquiryID(route.path),
      destinations: enquiryInfos.value?.your_trip?.destinations,
      trip_plan_name: enquiryOption?.trip_name || '',
      local_designer: enquiryOption?.designer ? enquiryOption?.designer?.name : '',
      has_estimated_price_range: enquiryInfos.value?.your_budget?.predict_price_range === undefined ? true : false,
      price_mode: dataStepEstimatedPrice.value?.showPricePerPerson ? 'price_per_person' : 'total_price',
      has_estimated_price_range_agreed: enquiryInfos.value?.your_budget?.has_budget == 1 ? true : false,
      has_exact_travel_date: enquiryInfos.value?.your_trip?.exact_date == 1 ? true : false,
      has_flight_booked: enquiryInfos.value?.your_trip?.has_flight_ticket == 1 ? true : false,
      has_answered_all_question: enquiryInfos.value?.is_completed,
      has_all_accommodation_selected: enquiryInfos.value?.how?.accm_pref?.length == 3 ? true : false,
    })
    sessionStorage.removeItem('in_confirm')
    sessionStorage.setItem('enquiry', JSON.stringify(dataSubmit))
    // sessionStorage.setItem('enquirydata', JSON.stringify(data))
    const path = route.path

    if (!isEmpty(enquiryAuthenticate.value)) {
      if (enquiryAuthenticate.value?.user_type === 'TV') {
        const COOKIE_DURATION = 365

        setCookie('dj_xxer_hash', enquiryAuthenticate.value?.hash, COOKIE_DURATION)
        setCookie('is_verified', 1, COOKIE_DURATION)
        await navigateTo({ path: enquiryAuthenticate.value?.redirect_url || REDIRECT_URL })
        // window.location.href = data.authenticate.redirect_url || REDIRECT_URL
        enquiryCaptureStore.removeRouteStore({ path, clearSession: true }) //clear local storage & session storage
        return
      } else {
        window.location.href = `${useAppConfig().publicDomainAgent}/login`
        enquiryCaptureStore.removeRouteStore({ path, clearSession: true }) //clear local storage & session storage
        return
      }
    } else {
      await navigateTo({ path: REDIRECT_URL })
      enquiryCaptureStore.removeRouteStore({ path, clearSession: true }) //clear local storage & session storage
      return
    }
  } catch (error) {
    console.log({ error })
    throw new Error(`handle submit final step error:`, { cause: error })
  }
}

function checkAnswerAll(data: any) {
  // if (parseInt(data?.how?.pax) > 2) {
  //   if (
  //     !data?.how?.ideal_room_config ||
  //     !data?.your_style?.suggestion ||
  //     (!data?.your_budget?.predict_price_range && !data?.your_budget?.budget && !data?.your_budget?.total_budget)
  //   ) {
  //     return false
  //   }
  // } else if (!data?.your_style?.suggestion || (!data?.your_budget?.predict_price_range && !data?.your_budget?.total_budget && !data?.your_budget?.budget)) {
  //   return false
  // }
  if(!data?.how?.ideal_room_config || (!data?.your_budget?.predict_price_range && !data?.your_budget?.total_budget && !data?.your_budget?.budget)) return false
  return true
}

function getArrayStepEnquiry() {
  let result = ENQUIRY_STEP_TESTING_VARIANT
  // let result = ENQUIRY_STEP_BASE
  // const country_code = getCookie('country_code')
  // if (route.name === 'withintrepid' || country_code !== COUNTRY_CODE_AUSTRALIA) {
  // if (route.name === 'withintrepid') {
  //   result = ENQUIRY_STEP_TESTING_VARIANT
  // }
  enquiryCaptureStore.setArrayStep(result)
}

const formData = reactive({
  isUIPopup: props.isUIPopup,
  heightForm: heightForm.value,
  labelClass: {
    titleClass: 'fw-700 gray-950 fs-32 lh-40 mb-fs-24 mb-lh-32',
    subTitleClass: 'gray-800 fs-18 lh-28 mb-fs-14 mb-lh-20',
    cssLabelField: 'fw-600 fs-18 lh-28 mb-fs-16 mb-lh-24 mb-4 db gray-950',
    submitButtonClass: 'button wp-100',
    cssLabelOptionField: 'fw-400 gray-700',
    inputExtraClass: '',
    stepsLabel: 'lh-28 fs-18 mb-lh-24 mb-fs-16 ttu gray-800 pb-16 mb-pb-0 mt-24 mb-mt-16',
    sectionForm: 'mb-16 mb-mb-12',
    divider: 'pb-16 bd-gray-300-new bd-b-d-2'
  },
  sessionStorageData: null,
})

watch(heightForm, (value) => {
  formData.heightForm = value
})
watch(() => props.stepBack, (step: EnquiryStepEnum) => {
  if(step) {
    selectStep(step)
  }
})

onMounted(async () => {
  // const enquiryMultiInfo = JSON.parse(sessionStorage.getItem(keyLocalStorage.enquiry))
  getArrayStepEnquiry()
  const enquiryMultiInfo = JSON.parse(localStorage.getItem(keyLocalStorage.enquiry)!)
  if(enquiryMultiInfo && enquiryMultiInfo[(route.path).toString()]) {
    stepActive.value = enquiryMultiInfo[(route.path).toString()]?.step || EnquiryStepEnum.Information
    // formData.sessionStorageData = enquiryMultiInfo[route.path]
  } else {
    stepActive.value = arrayStepEnquiry.value[firstIndex]
  }
})

const setWindowWidth = () => {
  generalStore.setWindowWidth()
}

if (process.client) {
  window.addEventListener('resize', setWindowWidth)
  window.addEventListener('load', setWindowWidth)
}

onUnmounted(() => {
  window.removeEventListener('resize', setWindowWidth)
  window.removeEventListener('load', setWindowWidth)
})
</script>

<template>
  <div class='hp-100'>
    <div class='sticky top-0 bg-white z-998' id='header-form' :style="isDesktop ? `height:${heightHeader}px` : ''">
      <div :class="['grid grid-template-columns-5 nav-form fs-0 lh-0', isDesktop ? 'h-10' : 'h-4', isUIPopup ? 'px-14' :'']">
        <div class='nav-item'
             v-for='n in 5' :key='n'
             :class="[{'nav-item-active': isUIPopup ? n === indexStepActive : (n <= indexStepActive)}, isUIPopup ? 'br-10 ml-2 mr-2' : '']">&nbsp;</div>
      </div>
    </div>
    <StepYourTrip
      v-show="stepActive === EnquiryStepEnum.YourTrip"
      :formData="formData"
      @change-destination="(des) => $emit('change-destination', des)"
      @nextStep="nextStep()"
      @prevStep="prevStep()"
    />
    <StepTravellersAccommodation
      v-show="stepActive === EnquiryStepEnum.How"
      :formData="formData"
      @nextStep="nextStep()"
      @prevStep="prevStep()"
    />
    <StepYourStyles
      v-show="stepActive === EnquiryStepEnum.YourStyle"
      :formData="formData"
      @nextStep="nextStep()"
      @prevStep="prevStep()"
    />
    <StepAboutYou
      v-show="stepActive === EnquiryStepEnum.Information"
      :formData="formData"
      @nextStep="nextStep()"
      @prevStep="prevStep()"
    />
    <StepEstimatedPrice
      v-show="stepActive === EnquiryStepEnum.YourBudget"
      :formData="formData"
      @nextStep="nextStep()"
      @prevStep="prevStep()"
    />
  </div>
</template>

<style scoped lang="scss">
.nav-form {
  .nav-item {
    background-color: brandColor(gray,200);
    &.nav-item-active {background-color: brandColor(brand,800);}
  }
}
</style>
